<template>
  <div
    class="wage-page home-wage-page"
    :class="{
      'home-wage-page--member': !showTooltips,
      'home-wage-page--magam': isWageReadOnly,
    }"
  >
    <div class="wage-layout home-wage-layout">
      <aside class="wage-sidebar wage-sidebar--facility home-wage-sidebar member-remote-sidebar">
        <div class="sidebar-scroll">
          <div class="sidebar-top">
            <div class="sidebar-ym-selector">
              <button type="button" class="ym-nav-btn" :disabled="listLoading" @click="changeMonth(-1)">
                <v-icon size="18">mdi-chevron-left</v-icon>
              </button>
              <div class="ym-display">
                <span class="ym-value">{{ selectedYear }}.{{ String(selectedMonth).padStart(2, '0') }}</span>
                <input
                  type="month"
                  :value="`${selectedYear}-${String(selectedMonth).padStart(2, '0')}`"
                  class="hidden-month-picker"
                  :aria-label="`${selectedYear}년 ${selectedMonth}월 선택`"
                  :disabled="listLoading"
                  @change="onMonthPickerChange"
                />
              </div>
              <div class="ym-magam-slot">
                <LoadingOverlay :show="wageMagamLoading" variant="light" />
                <template v-if="!wageMagamLoading">
                  <button
                    v-if="isWageMagamClosed"
                    type="button"
                    class="ym-magam-btn ym-magam-btn--done ym-magam-btn--clickable"
                    :disabled="wageMagamReleasing"
                    title="임금 마감 완료 — 클릭 시 마감 해제"
                    @click="releaseWageMagam"
                  >
                    <v-icon v-if="wageMagamReleasing" size="12" class="mdi-spin">mdi-loading</v-icon>
                    <template v-else>마감완료</template>
                  </button>
                  <button
                    v-else
                    type="button"
                    class="ym-magam-btn ym-magam-btn--ready"
                    :disabled="wageMagamClosing || listLoading"
                    :title="magamReadyButtonTitle"
                    @click="onCloseWageMagamClick"
                  >
                    <v-icon v-if="wageMagamClosing" size="12" class="mdi-spin">mdi-loading</v-icon>
                    <template v-else>마감하기</template>
                  </button>
                </template>
              </div>
              <button type="button" class="ym-nav-btn" :disabled="listLoading" @click="changeMonth(1)">
                <v-icon size="18">mdi-chevron-right</v-icon>
              </button>
            </div>

            <div
              v-if="!isWageMagamClosed && !wageMagamLoading"
              class="wage-magam-guide wage-magam-guide--tip"
            >
              <p class="wage-magam-guide__text">
                이번 달 임금 작성이 끝나면 「마감하기」를 눌러 주세요.
              </p>
            </div>

            <!-- 지급일·산재요율: 당월 마감 완료 시에만 잠금 (전월 미마감 잠금과 분리) -->
            <div class="sidebar-settings">
              <div class="setting-item">
                <span class="setting-label">지급일</span>
                <YmdDateInput
                  v-model="payDate"
                  class="setting-input"
                  :disabled="listLoading || isWageMagamClosed || savingMonthSettings"
                />
              </div>
              <div class="setting-item">
                <span class="setting-label">산재요율</span>
                <div class="setting-rate-input">
                  <input
                    v-model="sanRateDisplay"
                    type="text"
                    class="setting-input setting-rate-input__field"
                    placeholder="0.68"
                    inputmode="decimal"
                    :disabled="listLoading || isWageMagamClosed || savingMonthSettings"
                  />
                  <span class="setting-rate-input__suffix">%</span>
                </div>
              </div>
              <v-btn
                v-if="!isWageMagamClosed"
                variant="flat"
                class="btn-save sidebar-setting-save-btn"
                size="small"
                block
                :disabled="listLoading || savingMonthSettings"
                @click="handleSaveMonthSettings"
              >
                <v-icon v-if="savingMonthSettings" size="14" class="mdi-spin">mdi-loading</v-icon>
                <template v-else>저장</template>
              </v-btn>
            </div>

            <div class="home-wage-editable" :class="{ 'home-wage-editable--locked': isWageReadOnly }">
              <button
                type="button"
                class="sidebar-savings-btn"
                :disabled="!memberCode"
                @click="openNontaxSavings"
              >
                <v-icon size="16">mdi-piggy-bank-outline</v-icon>
                <span class="sidebar-savings-btn__text">
                  <span class="sidebar-savings-btn__label">인건비 절감</span>
                  <span v-if="sidebarMonthSaving != null" class="sidebar-savings-btn__value">
                    {{ formatNum(sidebarMonthSaving) }}원
                  </span>
                  <span v-else class="sidebar-savings-btn__sub">당월 절감액</span>
                </span>
              </button>
            </div>

            <button
              v-if="mainView !== 'list'"
              type="button"
              class="btn-show-all-list"
              @click="showAllList"
            >
              <v-icon size="14">mdi-format-list-bulleted</v-icon>
              사원전체목록보기
            </button>
          </div>

          <div class="sidebar-employee-panel">
            <div class="sidebar-list-header">
              <span class="list-header-title">
                <span class="list-header-dot"></span>
                사원 목록
              </span>
              <span class="list-header-count">{{ sortedEmployees.length }}명</span>
            </div>
            <div class="sidebar-search-wrap">
              <button
                type="button"
                class="sidebar-search-toggle"
                :class="{ 'sidebar-search-toggle--expanded': searchFiltersExpanded }"
                @click="searchFiltersExpanded = !searchFiltersExpanded"
              >
                <span class="sidebar-search-toggle__label">조건검색</span>
                <v-icon size="14">{{ searchFiltersExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </button>
              <v-expand-transition>
                <div v-show="searchFiltersExpanded" class="sidebar-search">
                  <div class="search-item">
                    <span class="search-label">사원 검색</span>
                    <div class="search-input-wrap">
                      <input
                        v-model="searchText"
                        type="text"
                        placeholder="이름..."
                        class="search-input"
                      />
                      <button v-if="searchText" type="button" class="search-clear" @click="searchText = ''">
                        <v-icon size="10">mdi-close</v-icon>
                      </button>
                    </div>
                  </div>
                  <div class="search-item">
                    <span class="search-label">직종 필터</span>
                    <select v-model="filterJobType" class="search-select">
                      <option value="">전체</option>
                      <option v-for="job in jobTypeOptions" :key="job" :value="job">{{ job }}</option>
                    </select>
                  </div>
                </div>
              </v-expand-transition>
            </div>
            <div v-if="!listLoading && sortedEmployees.length > 0" class="sidebar-list-columns">
              <span>이름</span>
              <span>직종</span>
            </div>
            <div class="sidebar-employee-list">
              <div v-if="listLoading" class="sidebar-list-loading">
                <v-progress-circular indeterminate color="#1e3a5f" size="20" width="2" />
              </div>
              <template v-else-if="sortedEmployees.length === 0 && excludedEmployees.length === 0">
                <p class="sidebar-list-empty">등록된 사원이 없습니다.</p>
              </template>
              <div
                v-for="row in sortedEmployees"
                :key="rowEmpKey(row)"
                class="sidebar-emp-item"
                :class="{ active: isSelectedEmployee(row) }"
              >
                <button
                  type="button"
                  class="sidebar-emp-item__select"
                  :disabled="listLoading || bulkLoading || removingEmployeeKey === rowEmpKey(row)"
                  @click="selectEmployee(row)"
                >
                  <div class="emp-item-main">
                    <span class="emp-item-name">{{ row.ce_name }}</span>
                    <span v-if="row.cwhd_is_owner === 1 || isJoinMonth(row) || isLeaveMonth(row)" class="emp-item-badges">
                      <span v-if="row.cwhd_is_owner === 1" class="emp-badge emp-badge--owner">대표</span>
                      <span v-if="isJoinMonth(row)" class="emp-badge emp-badge--join">입</span>
                      <span v-if="isLeaveMonth(row)" class="emp-badge emp-badge--leave">퇴</span>
                    </span>
                  </div>
                  <span class="emp-item-job">{{ row.ce_position || '-' }}</span>
                </button>
                <button
                  v-if="canRemoveEmployeeFromList(row)"
                  type="button"
                  class="sidebar-emp-item__remove"
                  title="이번 달 사원 목록에서 제거"
                  :disabled="listLoading || bulkLoading || isWageReadOnly || !!removingEmployeeKey"
                  @click.stop="removeEmployeeFromList(row)"
                >
                  <v-icon size="14">mdi-close</v-icon>
                </button>
              </div>
              <template v-if="excludedEmployees.length > 0">
                <p class="sidebar-excluded-label">목록에서 제거됨 {{ excludedEmployees.length }}명</p>
                <div
                  v-for="row in excludedEmployees"
                  :key="`ex-${rowEmpKey(row)}`"
                  class="sidebar-emp-item sidebar-emp-item--excluded"
                >
                  <div class="sidebar-emp-item__select sidebar-emp-item__select--static">
                    <div class="emp-item-main">
                      <span class="emp-item-name">{{ row.ce_name }}</span>
                    </div>
                    <span class="emp-item-job">{{ row.ce_position || '-' }}</span>
                  </div>
                  <button
                    type="button"
                    class="sidebar-emp-item__restore"
                    title="목록에 다시 넣기"
                    :disabled="listLoading || bulkLoading || isWageReadOnly || !!removingEmployeeKey"
                    @click.stop="restoreEmployeeToList(row)"
                  >
                    복원
                  </button>
                </div>
              </template>
            </div>
          </div>

          <div v-if="isAdmin" class="sidebar-migration-block">
            <p class="sidebar-migration-block__note">관리자 전용 · 엑셀 마이그레이션</p>
            <v-btn
              variant="flat"
              class="btn-refresh btn-migration"
              size="small"
              block
              :disabled="listLoading || bulkLoading"
              @click="showMigrationModal = true"
            >
              <v-icon size="14">mdi-file-excel</v-icon>
              임금 엑셀 업로드
            </v-btn>
            <v-btn
              variant="flat"
              class="btn-refresh btn-migration"
              size="small"
              block
              :disabled="listLoading || bulkLoading"
              @click="showLedgerConvertModal = true"
            >
              <v-icon size="14">mdi-swap-horizontal</v-icon>
              임금대장 치환(테스트)
            </v-btn>
          </div>

          <div class="sidebar-footer-block">
            <button
              type="button"
              class="sidebar-labor-cost-btn sidebar-labor-cost-btn--above-tax"
              :disabled="loadingLaborCostModal"
              @click="openLaborCostModal"
            >
              <v-icon v-if="loadingLaborCostModal" size="14" class="mdi-spin">mdi-loading</v-icon>
              <v-icon v-else size="14">mdi-chart-pie</v-icon>
              인건비지출내역
            </button>

          <div v-if="taxRateInfo" class="sidebar-bottom">
            <div class="sidebar-tax-info">
              <div class="tax-info-title-row">
                <button
                  type="button"
                  class="tax-info-toggle"
                  :class="{ 'tax-info-toggle--expanded': taxRatePanelExpanded }"
                  @click="taxRatePanelExpanded = !taxRatePanelExpanded"
                >
                  <span class="tax-info-title">4대보험·세율 ({{ taxRateInfo.year }}년)</span>
                  <v-icon size="14">{{ taxRatePanelExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </button>
                <v-btn
                  v-if="canEditTaxRate && taxRatePanelExpanded"
                  variant="flat"
                  class="btn-edit tax-info-edit-btn"
                  size="x-small"
                  @click="openTaxRateEditModal"
                >
                  수정
                </v-btn>
              </div>
              <div v-show="taxRatePanelExpanded" class="tax-info-body">
                <div v-if="taxRateFallback" class="tax-info-note">※ 최근 연도 세율 적용</div>
                <div v-for="taxRow in taxRateDisplayRows" :key="taxRow.label" class="tax-info-row">
                  <span>{{ taxRow.label }}</span>
                  <span>{{ taxRow.value }}</span>
                </div>
                <div v-if="isAdmin" class="sidebar-wage-delete">
                  <p class="sidebar-wage-delete__admin-note">관리자 전용 · 관리자만 표시됩니다</p>
                  <p class="sidebar-wage-delete__hint">
                    {{ selectedYear }}년 {{ selectedMonth }}월 · 해당 연월 임금 데이터 전체 삭제
                  </p>
                  <v-btn
                    variant="flat"
                    class="btn-delete sidebar-wage-delete__btn"
                    size="small"
                    block
                    :disabled="listLoading || bulkLoading || filteredRows.length === 0"
                    @click="handleDeleteAllMonth"
                  >
                    <v-icon v-if="bulkLoading" size="14" class="mdi-spin">mdi-loading</v-icon>
                    <template v-else>당월 임금 전체삭제</template>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </aside>

      <div class="wage-main-content home-wage-main">
        <template v-if="mainView === 'list'">
          <div class="home-wage-content-shell home-wage-content-shell--list">
            <LoadingOverlay :show="listLoading" text="불러오는 중..." variant="light" />
            <LoadingOverlay :show="bulkLoading" text="처리 중..." variant="light" />
            <LoadingOverlay
              :show="savingAll"
              :text="`저장 중... (${savingAllProgress.current}/${savingAllProgress.total})`"
              variant="save"
            />

            <div class="home-wage-toolbar">
              <div class="toolbar-left">
                <div class="toolbar-title">
                  <v-icon size="20" color="#1e3a5f">mdi-format-list-bulleted</v-icon>
                  <span>사원별 급여 현황</span>
                </div>
                <span class="toolbar-count">{{ sortedEmployees.length }}명</span>
                <div class="toolbar-report-actions">
                  <CommonWageReportMenu
                    :saved-wage-count="savedWageCount"
                    :employee-count="filteredRows.length"
                    :can-view-accounting-report="canViewAccountingReport"
                    :slip-disabled="savedWageCount === 0"
                    :is-admin="isAdmin"
                    slip-label="명세서 PDF"
                    @accounting-report="downloadAccountingReport"
                    @client-report="downloadClientReport"
                    @wage-slip="downloadWageSlip"
                    @nontax-monthly="openNontaxCompare"
                    @nontax-yearly="openNontaxCompareYearly"
                    @transaction="openTransactionModal"
                  />
                  <WageSlipBroadcastButton
                    :is-closed="isWageMagamClosed"
                    :member-code="memberCode"
                    :year-month="yearMonth"
                    :biz-gubun="selectedBizGubun"
                  />
                </div>
              </div>
              <div class="toolbar-right">
                <v-btn
                  variant="flat"
                  class="btn-refresh toolbar-import-btn"
                  size="small"
                  :disabled="listLoading || bulkLoading || isWageReadOnly"
                  @click="handleCopyFromPrev"
                >
                  <v-icon size="14">mdi-content-copy</v-icon>
                  전월복사
                </v-btn>
                <v-btn
                  variant="flat"
                  class="btn-save"
                  size="small"
                  :disabled="listLoading || bulkLoading || savingAll || filteredRows.length === 0 || isWageReadOnly"
                  @click="saveAllData"
                >
                  <v-icon v-if="savingAll" size="14" class="mdi-spin">mdi-loading</v-icon>
                  <template v-else>모두저장</template>
                </v-btn>
              </div>
            </div>

            <div class="home-wage-table-wrap">
              <table class="home-wage-table" :class="{ 'home-wage-table--loading': listLoading }">
                <thead>
                  <tr>
                    <th class="col-no">No</th>
                    <th class="sortable col-name" @click="toggleSort('name')">
                      사원명 <v-icon size="12">{{ getSortIcon('name') }}</v-icon>
                    </th>
                    <th class="sortable" @click="toggleSort('job')">
                      직종 <v-icon size="12">{{ getSortIcon('job') }}</v-icon>
                    </th>
                    <th class="sortable col-wage" @click="toggleSort('monthlyPay')">
                      월급여 <v-icon size="12">{{ getSortIcon('monthlyPay') }}</v-icon>
                    </th>
                    <th class="sortable col-wage" @click="toggleSort('corpInsurance')">
                      기관부담보험 <v-icon size="12">{{ getSortIcon('corpInsurance') }}</v-icon>
                    </th>
                    <th class="sortable col-wage" @click="toggleSort('retirement')">
                      퇴직금 <v-icon size="12">{{ getSortIcon('retirement') }}</v-icon>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="sortedEmployees.length === 0">
                    <td colspan="6" class="empty-row">등록된 사원이 없습니다.</td>
                  </tr>
                  <tr
                    v-for="(row, idx) in sortedEmployees"
                    :key="rowEmpKey(row)"
                    class="row-clickable"
                    :class="{ 'row-selected': isSelectedEmployee(row) }"
                    @click="selectEmployee(row)"
                  >
                    <td class="col-no">{{ idx + 1 }}</td>
                    <td class="col-name">
                      <div class="name-cell">
                        <div class="avatar" :style="{ background: getAvatarColor(row.ce_name) }">
                          {{ getInitial(row.ce_name) }}
                        </div>
                        <div class="name-cell-text">
                          <span class="name-cell-label">{{ row.ce_name }}</span>
                          <span v-if="row.cwhd_is_owner === 1 || isJoinMonth(row) || isLeaveMonth(row)" class="emp-item-badges">
                            <span v-if="row.cwhd_is_owner === 1" class="emp-badge emp-badge--owner">대표</span>
                            <span v-if="isJoinMonth(row)" class="emp-badge emp-badge--join">입</span>
                            <span v-if="isLeaveMonth(row)" class="emp-badge emp-badge--leave">퇴</span>
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>{{ row.ce_position || '-' }}</td>
                    <td class="col-wage">{{ formatListWageAmount(row, 'monthlyPay') }}</td>
                    <td class="col-wage">{{ formatListWageAmount(row, 'corpInsurance') }}</td>
                    <td class="col-wage">{{ formatListWageAmount(row, 'retirement') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>

        <template v-else-if="mainView === 'detail' && selectedRow">
          <div class="home-wage-content-shell home-wage-content-shell--detail">
            <LoadingOverlay :show="listLoading" text="불러오는 중..." variant="light" />
            <LoadingOverlay :show="bulkLoading" text="처리 중..." variant="light" />
            <LoadingOverlay :show="savingDetail" text="저장 중..." variant="save" />
            <LoadingOverlay :show="importingPrevMonth" text="데이터 불러오는 중..." />

            <div class="home-wage-toolbar">
              <div class="toolbar-left">
                <button type="button" class="toolbar-back-btn" @click="showAllList">
                  <v-icon size="16">mdi-arrow-left</v-icon>
                  목록
                </button>
                <div class="toolbar-title">
                  <div class="toolbar-name-nav">
                    <button
                      type="button"
                      class="toolbar-emp-nav-btn"
                      :disabled="!canNavigatePrevEmployee || isEmployeeNavDisabled"
                      aria-label="이전 사원"
                      @click="navigatePrevEmployee"
                    >
                      <v-icon size="18">mdi-chevron-left</v-icon>
                    </button>
                    <span class="toolbar-emp-name">{{ selectedRow.ce_name }}</span>
                    <button
                      type="button"
                      class="toolbar-emp-nav-btn"
                      :disabled="!canNavigateNextEmployee || isEmployeeNavDisabled"
                      aria-label="다음 사원"
                      @click="navigateNextEmployee"
                    >
                      <v-icon size="18">mdi-chevron-right</v-icon>
                    </button>
                  </div>
                  <span class="toolbar-job">{{ selectedRow.ce_position || '-' }}</span>
                  <span class="toolbar-tenure">{{ formatEmpTenureChip(selectedRow) }}</span>
                  <span v-if="formatEmpAgeChip(selectedRow)" class="toolbar-age">{{ formatEmpAgeChip(selectedRow) }}</span>
                </div>
                <div class="toolbar-report-actions">
                  <v-btn
                    variant="flat"
                    class="btn-refresh"
                    size="small"
                    :disabled="!selectedRow.cwhd_idx"
                    @click="downloadWageSlipForSelected"
                  >
                    <v-icon size="14">mdi-file-pdf-box</v-icon>
                    명세서 PDF
                  </v-btn>
                </div>
              </div>
              <div class="toolbar-right">
                <v-btn
                  variant="flat"
                  class="btn-refresh toolbar-import-btn"
                  size="small"
                  :disabled="listLoading || bulkLoading || importingPrevMonth || savingDetail || isWageReadOnly"
                  @click="importPrevMonthForSelected"
                >
                  <v-icon v-if="importingPrevMonth" size="14" class="mdi-spin">mdi-loading</v-icon>
                  <template v-else>지난달 데이터 가져오기</template>
                </v-btn>
                <v-btn
                  variant="flat"
                  class="btn-save"
                  size="small"
                  :disabled="listLoading || bulkLoading || savingDetail || isWageReadOnly"
                  @click="saveSelectedWage"
                >
                  <v-icon v-if="savingDetail" size="14" class="mdi-spin">mdi-loading</v-icon>
                  <template v-else>저장</template>
                </v-btn>
              </div>
            </div>

            <div class="detail-wage-board home-wage-editable" :class="{ 'home-wage-editable--locked': isWageReadOnly }">
              <CommonWageEditModal
                :key="`${rowEmpKey(selectedRow)}-${selectedYear}-${selectedMonth}`"
                ref="wageEditModalRef"
                inline
                :show="true"
                :row="selectedRow"
                :employees="sortedEmployees"
                :m-code="memberCode"
                :year="selectedYear"
                :month="selectedMonth"
                :biz-gubun="selectedBizGubun"
                :min-hourly-wage="taxRateInfo?.minHourlyWage"
                :tax-rate-info="effectiveTaxRateInfo"
                :auto-calc-mode="autoCalcMode"
                :basic-calc-mode-allowed="canUseBasicCalcMode"
                :default-work-days="defaultWorkDays"
                @saved="onDetailSaved"
                @deleted="onDetailDeleted"
                @change="markDirty"
                @hydrated="clearDirty"
              />
            </div>
          </div>
        </template>
      </div>
    </div>

    <CommonWageReportExcel
      ref="wageReportRef"
      :year="selectedYear"
      :month="selectedMonth"
      :wage-data="savedWageRows"
      :member-name="memberData?.value?.m_name || memberData?.m_name"
      :tax-rate-info="effectiveTaxRateInfo"
      :income-tax-table="incomeTaxTable"
      :snapshot-only="wageMonthSnapshot"
      @error="onWageReportError"
    />

    <CommonWageNontaxCompare
      ref="nontaxCompareRef"
      :wage-list="savedWageRows"
      :tax-rate="taxRate"
      :income-tax-table="incomeTaxTable"
      :year="selectedYear"
      :month="selectedMonth"
      :member-name="memberData?.value?.m_name || memberData?.m_name"
    />

    <CommonWageNontaxCompareYearly
      ref="nontaxCompareYearlyRef"
      :m-code="memberCode"
      :member-name="memberData?.value?.m_name || memberData?.m_name"
      :biz-gubun="selectedBizGubun"
    />

    <!-- SMS 모달: 비활성 (활성화 시 주석 해제) -->
    <!--
    <CommonWageSmsModal
      ref="smsModalRef"
      :m-code="memberCode"
      :member-name="memberDisplayName"
      :biz-gubun="selectedBizGubun"
      :year="selectedYear"
      :month="selectedMonth"
    />

    <CommonWageSmsTestModal
      v-if="isAdmin"
      ref="smsTestModalRef"
      :m-code="memberCode"
      :member-name="memberDisplayName"
      :biz-gubun="selectedBizGubun"
      :year="selectedYear"
      :month="selectedMonth"
    />
    -->

    <CommonWageTransaction
      v-if="isAdmin"
      ref="transactionRef"
      :m-code="memberCode"
      :member-name="memberDisplayName"
      :biz-gubun="selectedBizGubun"
      :year="selectedYear"
      :month="selectedMonth"
      :data-count="filteredRows.length"
    />

    <WageExcelMigrationModal
      v-if="isAdmin"
      v-model="showMigrationModal"
      wage-type="facility"
      :m-code="memberCode"
      :year-month="migrationYearMonth"
      :biz-gubun="selectedBizGubun"
      :pay-date="payDate || null"
      @imported="onMigrationImported"
    />

    <WageLedgerConvertModal
      v-if="isAdmin"
      v-model="showLedgerConvertModal"
      wage-type="facility"
    />

    <WageSsnMissingModal
      v-model="ssnMissingModalOpen"
      :member-code="memberCode"
      :year-month="yearMonth"
      @saved="onSsnMissingSaved"
      @continue-magam="onSsnMissingContinueMagam"
    />

    <CommonWageLaborCostModal
      ref="laborCostModalRef"
      :member-code="memberCode"
      :year="selectedYear"
      :month="selectedMonth"
      :biz-gubun="selectedBizGubun"
    />

    <v-dialog v-model="showTaxRateEditModal" max-width="360" persistent>
      <div class="home-wage-tax-rate-edit-dialog">
        <LoadingOverlay :show="savingTaxRate" text="저장 중..." variant="save" />
        <div class="home-wage-tax-rate-edit-dialog__header">
          <span class="header-title">세율 수정 ({{ taxRateEditTargetYear }}년)</span>
          <span class="admin-only-chip">관리자전용</span>
          <button
            type="button"
            class="home-wage-tax-rate-edit-dialog__close"
            :disabled="savingTaxRate"
            aria-label="닫기"
            @click="closeTaxRateEditModal"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="home-wage-tax-rate-edit-dialog__body">
          <p v-if="taxRateFallback" class="edit-note">
            {{ taxRateInfo?.year }}년 세율이 표시 중입니다. 저장하면 {{ taxRateEditTargetYear }}년 세율이 생성됩니다.
          </p>
          <div
            v-for="field in taxRateEditFields"
            :key="field.key"
            class="edit-field"
          >
            <label class="edit-label">{{ field.label }}</label>
            <div class="edit-input-wrap">
              <input
                v-model="taxRateEditForm[field.key]"
                type="text"
                class="edit-input"
                :inputmode="field.isLaborRate ? 'decimal' : 'decimal'"
                @blur="onTaxRateEditBlur(field.key)"
              />
              <span v-if="field.unit" class="edit-unit">{{ field.unit }}</span>
            </div>
          </div>
        </div>
        <div class="home-wage-tax-rate-edit-dialog__footer">
          <v-btn variant="text" class="btn-cancel" :disabled="savingTaxRate" @click="closeTaxRateEditModal">
            취소
          </v-btn>
          <v-btn variant="flat" class="btn-save" :disabled="savingTaxRate" @click="saveTaxRateEdit">
            <v-icon v-if="savingTaxRate" size="14" class="mdi-spin">mdi-loading</v-icon>
            저장
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, inject, provide, type Ref } from 'vue';
import YmdDateInput from '@/components/common/YmdDateInput.vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { commonWageApi, homeWageApi, tokenManager } from '@/services/api';
import CommonWageEditModal from '@/components/wage/common/CommonWageEditModal.vue';
import CommonWageReportMenu from '@/components/wage/common/CommonWageReportMenu.vue';
import WageSlipBroadcastButton from '@/components/wage/common/WageSlipBroadcastButton.vue';
import CommonWageReportExcel from '@/components/wage/common/CommonWageReportExcel.vue';
import CommonWageNontaxCompare from '@/components/wage/common/CommonWageNontaxCompare.vue';
import CommonWageNontaxCompareYearly from '@/components/wage/common/CommonWageNontaxCompareYearly.vue';
// SMS 비활성 — 활성화 시 import 주석 해제
// import CommonWageSmsModal from '@/components/wage/common/CommonWageSmsModal.vue';
// import CommonWageSmsTestModal from '@/components/wage/common/CommonWageSmsTestModal.vue';
import CommonWageTransaction from '@/components/wage/common/CommonWageTransaction.vue';
import CommonWageLaborCostModal from '@/components/wage/common/CommonWageLaborCostModal.vue';
import WageExcelMigrationModal from '@/components/wage/WageExcelMigrationModal.vue';
import WageSsnMissingModal from '@/components/wage/WageSsnMissingModal.vue';
import WageLedgerConvertModal from '@/components/wage/WageLedgerConvertModal.vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import {
  parseJoinDate,
  calcTenureMonths,
  formatHomeWageRatePercent,
  parseHomeWageRatePercentInput,
  parseHomeWageTaxRateValue,
} from '@/policies/homeWageSettingPolicy';
import {
  getCommonWageLedgerGrossPay,
  getCommonWageLedgerEmployerInsurance,
  getCommonWageLedgerRetirement,
  getCommonWageCenterSaving,
  previewCommonWageRetirement,
  sumCommonWagePayParts,
} from '@/policies/commonWageSettingPolicy';
import {
  bulkSaveCommonWageRows,
  bulkRecalcCopiedDeductions,
  needsCopiedDeductionRecalc,
} from '@/composables/wage/useCommonWageBulkSave';
import { useCommonWageTableDraft } from '@/composables/wage/useCommonWageTableDraft';
import { useWageMemberYearMonth } from '@/composables/useMemberYearMonth';
import {
  useWageMagam,
  getPrevYearMonth,
  formatYearMonthLabel,
  buildMagamBlockButtonTitle,
} from '@/composables/wage/useWageMagam';
import type { IncomeTaxRow } from '@/composables/wage/useCommonWageCalc';
import type { CommonWageListRow, CommonWageListStats, CommonWageTaxRate } from '@/types/commonWage';
import { compareJobThenName, getJobTypeSortRank } from '@/policies/jobTypeSort';
import { rowEmpKey } from '@/utils/empMatchKey';
import { countMonthBusinessDays } from '@/utils/monthBusinessDays';

const route = useRoute();
const router = useRouter();
const store = useStore();
const memberData = inject<any>('memberData');
const selectedBizGubunRef = inject<Ref<string>>('selectedBizGubun');
const selectedBizGubun = computed(() => selectedBizGubunRef?.value || '');

const isHomeCareBiz = (biz: string) => biz === '방문요양' || biz === '방문목욕';

const memberCode = computed(
  () => (route.params.memberCode as string) || memberData?.value?.m_code || ''
);

const isPopupMode = computed(() => !!route.params.memberCode);

const homeRouteName = computed(() =>
  isPopupMode.value ? 'memberDetailWageMonthlySettingHome' : 'memberWageMonthlySettingHome'
);

const {
  selectedYear,
  selectedMonth,
  changeMonth: rawChangeMonth,
  onMonthPickerChange: rawMonthPickerChange,
  setYearMonth,
} = useWageMemberYearMonth(memberCode);

const yearMonth = computed(
  () => `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`
);

/** 이 연월 마감 조회가 끝났고, 실제로 마감된 경우에만 저장 스냅샷 */
const wageMonthSnapshot = computed(
  () => magamResolvedYearMonth.value === yearMonth.value && isWageMagamClosed.value
);

const {
  isClosed: isWageMagamClosed,
  resolvedYearMonth: magamResolvedYearMonth,
  loading: wageMagamLoading,
  closing: wageMagamClosing,
  releasing: wageMagamReleasing,
  isReadOnly: isWageReadOnly,
  ssnMissingCount,
  joinMissingCount,
  ssnMissingBlocksClose,
  closeMagam: closeWageMagam,
  releaseMagam: releaseWageMagam,
  loadStatus: loadWageMagamStatus,
} = useWageMagam({
  memberCode,
  yearMonth,
  bizGubun: selectedBizGubun,
  hasPrevWageData: async () => {
    const prevYm = getPrevYearMonth(yearMonth.value);
    if (!memberCode.value || !prevYm || prevYm.length < 6) return false;
    try {
      const prevYear = parseInt(prevYm.slice(0, 4), 10);
      const prevMonth = parseInt(prevYm.slice(4, 6), 10);
      const res = await commonWageApi.getList(memberCode.value, {
        year: prevYear,
        month: prevMonth,
        bizGubun: selectedBizGubun.value || undefined,
      });
      const rows = res.data?.data;
      return (
        Array.isArray(rows) &&
        rows.some(
          (r: any) =>
            Number(r?.cwhd_list_excluded) !== 1 &&
            (r.cwhd_idx || r.cwhd_wage_total != null)
        )
      );
    } catch {
      return false;
    }
  },
});

const ssnMissingModalOpen = ref(false);

const onCloseWageMagamClick = async () => {
  if (ssnMissingBlocksClose.value) {
    ssnMissingModalOpen.value = true;
    return;
  }
  await closeWageMagam();
};

const onSsnMissingSaved = async () => {
  await loadWageMagamStatus();
};

const onSsnMissingContinueMagam = async () => {
  ssnMissingModalOpen.value = false;
  await loadWageMagamStatus();
  if (ssnMissingBlocksClose.value) {
    ssnMissingModalOpen.value = true;
    return;
  }
  await closeWageMagam();
};

const magamReadyButtonTitle = computed(() =>
  buildMagamBlockButtonTitle(ssnMissingCount.value, joinMissingCount.value)
);

// ═══════════════════════════════════════════════════════════════════════
// ★★★ 저장하지 않은 변경사항 추적 (Unsaved Changes Guard) ★★★
// ═══════════════════════════════════════════════════════════════════════
/** 현재 상세 화면에서 저장하지 않은 변경사항이 있는지 */
const hasUnsavedChanges = ref(false);

/** 저장 완료 또는 초기화 시 호출 — dirty 플래그 해제 */
const clearDirty = () => {
  hasUnsavedChanges.value = false;
};

/** 저장하지 않은 변경사항 확인 — true면 진행, false면 취소 */
const confirmUnsavedChanges = (): boolean => {
  if (!hasUnsavedChanges.value) return true;
  return window.confirm(
    '저장하지 않은 변경사항이 있습니다.\n\n저장하지 않고 계속 진행하시겠습니까?'
  );
};

/** ★ 월 변경 래핑 — 저장하지 않은 변경사항 확인 */
const changeMonth = (delta: number) => {
  if (!confirmUnsavedChanges()) return;
  clearDirty();
  rawChangeMonth(delta);
};

/** ★ 월 선택기 래핑 — 저장하지 않은 변경사항 확인 */
const onMonthPickerChange = (event: Event) => {
  if (!confirmUnsavedChanges()) {
    const input = event.target as HTMLInputElement;
    input.value = `${selectedYear.value}-${String(selectedMonth.value).padStart(2, '0')}`;
    return;
  }
  clearDirty();
  rawMonthPickerChange(event);
};
type MainView = 'list' | 'detail';
type ListSortKey = 'default' | 'name' | 'job' | 'monthlyPay' | 'corpInsurance' | 'retirement';

const payDate = ref('');
const savingMonthSettings = ref(false);
const sanRate = ref(0.0068);
const sanRateDisplay = ref('0.68');
const formatSanRateDisplay = (value: number | string): string =>
  formatHomeWageRatePercent(value, { withSuffix: false });
const listLoading = ref(false);
const bulkLoading = ref(false);
const mainView = ref<MainView>('list');
const selectedRow = ref<CommonWageListRow | null>(null);

/** 변경사항 발생 시 호출 — dirty 플래그 설정 (상세 화면에서만) */
const markDirty = () => {
  if (mainView.value === 'detail' && selectedRow.value) {
    hasUnsavedChanges.value = true;
  }
};
const searchFiltersExpanded = ref(false);
const taxRatePanelExpanded = ref(false);
const filterJobType = ref('');
const sortKey = ref<ListSortKey>('default');
const sortOrder = ref<'asc' | 'desc'>('asc');
const wageReportRef = ref<InstanceType<typeof CommonWageReportExcel> | null>(null);
const nontaxCompareRef = ref<InstanceType<typeof CommonWageNontaxCompare> | null>(null);
const nontaxCompareYearlyRef = ref<InstanceType<typeof CommonWageNontaxCompareYearly> | null>(null);
// const smsModalRef = ref<InstanceType<typeof CommonWageSmsModal> | null>(null);
// const smsTestModalRef = ref<InstanceType<typeof CommonWageSmsTestModal> | null>(null);
const transactionRef = ref<InstanceType<typeof CommonWageTransaction> | null>(null);
const searchText = ref('');
const savingAll = ref(false);
const savingAllProgress = ref({ current: 0, total: 0 });
const savingDetail = ref(false);
const importingPrevMonth = ref(false);
const wageEditModalRef = ref<InstanceType<typeof CommonWageEditModal> | null>(null);
const incomeTaxTable = ref<IncomeTaxRow[]>([]);

/** 자동계산 — 항상 ON (계산 버튼 없이 자동으로 계산됨) */
const autoCalcMode = ref(true);

const roleLevel = computed(() => Number(store.getters.actualRoleLevel ?? store.getters.roleLevel ?? 0));
const isAdmin = computed(() => roleLevel.value >= 60);
const canEditTaxRate = computed(() => roleLevel.value >= 60);
/** 기본급 산출 방식 토글 — 관리자 권한(80) 이상 전용 */
const canUseBasicCalcMode = computed(() => roleLevel.value >= 80);
/** 매니저(50+)·직원(40+) 툴팁 — 기관회원(고객) 로그인 시 숨김. 실비변상 등은 member-visible */
const showTooltips = computed(() => roleLevel.value >= 40);
provide('showWageTooltips', showTooltips);
const showMigrationModal = ref(false);
const showLedgerConvertModal = ref(false);
const laborCostModalRef = ref<InstanceType<typeof CommonWageLaborCostModal> | null>(null);
const loadingLaborCostModal = ref(false);
const showTaxRateEditModal = ref(false);
const savingTaxRate = ref(false);
const taxRateEditTargetYear = ref(new Date().getFullYear());

type TaxRateEditFieldKey =
  | 'rateYeon'
  | 'rateGun'
  | 'rateGo'
  | 'rateJang'
  | 'rateSil'
  | 'rateSan'
  | 'laborCostRate';

const taxRateEditFields: { key: TaxRateEditFieldKey; label: string; unit: string; isLaborRate?: boolean }[] = [
  { key: 'rateYeon', label: '국민연금', unit: '%' },
  { key: 'rateGun', label: '건강보험', unit: '%' },
  { key: 'rateGo', label: '고용보험', unit: '%' },
  { key: 'rateJang', label: '장기요양', unit: '%' },
  { key: 'rateSil', label: '실업급여', unit: '%' },
  { key: 'rateSan', label: '산재보험', unit: '%' },
  { key: 'laborCostRate', label: '인건비율', unit: '%', isLaborRate: true },
];

const taxRateEditForm = ref<Record<TaxRateEditFieldKey, string>>({
  rateYeon: '',
  rateGun: '',
  rateGo: '',
  rateJang: '',
  rateSil: '',
  rateSan: '',
  laborCostRate: '',
});

const migrationYearMonth = computed(
  () => `${selectedYear.value}${String(selectedMonth.value).padStart(2, '0')}`
);

const onMigrationImported = async () => {
  await loadWageList();
};

/** 회계처리용 임금대장 — 매니저(50+) 이상, 기관회원 제외 */
const canViewAccountingReport = computed(() => store.getters.canViewAccountingWageReport);

const memberDisplayName = computed(
  () => memberData?.value?.m_name || memberData?.m_name || ''
);

const wageRows = ref<CommonWageListRow[]>([]);
/** 빠른 월 이동 시 stale API 응답 무시 */
const monthDataLoadSeq = ref(0);
const stats = ref<CommonWageListStats>({
  totalCount: 0,
  totalWage: 0,
  totalLDeduction: 0,
  totalCDeduction: 0,
});
const taxRateInfo = ref<CommonWageTaxRate | null>(null);
const taxRateFallback = ref(false);

const minHourlyWageRef = computed(() => taxRateInfo.value?.minHourlyWage);

const holidayDates = ref<string[]>([]);
const holidaysReady = ref(false);
let holidayLoadSeq = 0;

const loadHolidayDates = async (year: number) => {
  const seq = ++holidayLoadSeq;
  holidaysReady.value = false;
  try {
    const res = await homeWageApi.getHolidays({ year });
    if (seq !== holidayLoadSeq) return;
    const dates: string[] = [];
    for (const item of res.data?.data || []) {
      const date = String(item?.date || '').substring(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) dates.push(date);
    }
    holidayDates.value = dates;
  } catch (error) {
    if (seq !== holidayLoadSeq) return;
    console.error('공휴일 조회 오류:', error);
    holidayDates.value = [];
  } finally {
    if (seq === holidayLoadSeq) holidaysReady.value = true;
  }
};

/** 근무일수 미입력 시: 그 달의 토·일·공휴일을 뺀 일수. 조회 전에는 null */
const defaultWorkDays = computed<number | null>(() => {
  if (!holidaysReady.value) return null;
  return countMonthBusinessDays(selectedYear.value, selectedMonth.value, holidayDates.value);
});

watch(selectedYear, (year) => {
  void loadHolidayDates(year);
}, { immediate: true });

const tableDraft = useCommonWageTableDraft({
  minHourlyWage: minHourlyWageRef,
  taxRateInfo,
  incomeTaxTable,
  year: selectedYear,
  month: selectedMonth,
  mCode: memberCode,
  bizGubun: selectedBizGubun,
  defaultWorkDays,
});

const effectiveTaxRateInfo = computed<CommonWageTaxRate | null>(() => {
  if (!taxRateInfo.value) return null;
  if (sanRate.value > 0) {
    return { ...taxRateInfo.value, rateSan: sanRate.value };
  }
  return taxRateInfo.value;
});

const taxRate = computed(() => ({
  tr_yeon: Number(taxRateInfo.value?.rateYeon) || 0.045,
  tr_gun: Number(taxRateInfo.value?.rateGun) || 0.03545,
  tr_jang: Number(taxRateInfo.value?.rateJang) || 0.1295,
  tr_go: Number(taxRateInfo.value?.rateGo) || 0.009,
  tr_sil: Number(taxRateInfo.value?.rateSil) || 0.009,
  tr_san: sanRate.value > 0 ? sanRate.value : Number(taxRateInfo.value?.rateSan) || 0.007,
}));

const taxRateDisplayRows = computed(() => {
  const t = taxRateInfo.value;
  if (!t) return [];
  const san = sanRate.value > 0 ? sanRate.value : parseHomeWageTaxRateValue(t.rateSan, 0.007);
  return [
    { label: '최저시급', value: `${formatNum(t.minHourlyWage)}원` },
    { label: '국민연금', value: formatHomeWageRatePercent(t.rateYeon) },
    { label: '건강보험', value: formatHomeWageRatePercent(t.rateGun) },
    { label: '고용보험', value: formatHomeWageRatePercent(t.rateGo) },
    { label: '장기요양', value: formatHomeWageRatePercent(t.rateJang) },
    { label: '실업급여', value: formatHomeWageRatePercent(t.rateSil) },
    { label: '산재보험', value: formatHomeWageRatePercent(san) },
    { label: '인건비율', value: formatHomeWageRatePercent(t.laborCostRate, { scaleFromDecimal: false }) },
  ];
});

const sidebarMonthSaving = computed(() => {
  if (savedWageRows.value.length === 0) return null;
  return savedWageRows.value.reduce((sum, row) => sum + getCommonWageCenterSaving(row), 0);
});

const jobTypeOptions = computed(() => {
  const jobs = new Set<string>();
  wageRows.value.forEach((row) => {
    if (!isListExcluded(row) && row.ce_position) jobs.add(row.ce_position);
  });
  return Array.from(jobs).sort((a, b) => compareJobThenName(a, '', b, ''));
});

/** 마감이 확인된 달만 저장 퇴직금. 그 외에는 한도 중식비 기준 */
const retirementAmountForList = (row: CommonWageListRow) => {
  if (wageMonthSnapshot.value || !row.cwhd_idx) {
    return getCommonWageLedgerRetirement(row);
  }
  return previewCommonWageRetirement(row, selectedYear.value, selectedMonth.value);
};

const getListSortValue = (row: CommonWageListRow, key: 'monthlyPay' | 'corpInsurance' | 'retirement') => {
  if (key === 'monthlyPay') return getCommonWageLedgerGrossPay(row);
  if (key === 'corpInsurance') return getCommonWageLedgerEmployerInsurance(row);
  return retirementAmountForList(row);
};

const getJobRank = (job: string): number => getJobTypeSortRank(job);

const isListExcluded = (row: CommonWageListRow) =>
  Number(row.cwhd_list_excluded) === 1 || row.cwhd_list_excluded === true;

const filterWageRowsBySearch = (list: CommonWageListRow[]) => {
  let next = [...list];
  const q = searchText.value.trim().toLowerCase();
  if (q) {
    next = next.filter((row) => row.ce_name?.toLowerCase().includes(q));
  }
  if (filterJobType.value) {
    next = next.filter((row) => row.ce_position === filterJobType.value);
  }
  return next;
};

const sortedEmployees = computed(() => {
  let list = filterWageRowsBySearch(wageRows.value.filter((row) => !isListExcluded(row)));

  list.sort((a, b) => {
    // 대표자는 항상 맨 위 (직종/정렬 조건과 관계없이)
    const ownerA = a.cwhd_is_owner === 1;
    const ownerB = b.cwhd_is_owner === 1;
    if (ownerA && !ownerB) return -1;
    if (!ownerA && ownerB) return 1;

    const nameA = a.ce_name || '';
    const nameB = b.ce_name || '';
    const jobA = a.ce_position || '';
    const jobB = b.ce_position || '';

    if (sortKey.value === 'name') {
      const cmp = nameA.localeCompare(nameB, 'ko');
      return sortOrder.value === 'asc' ? cmp : -cmp;
    }
    if (sortKey.value === 'job') {
      const cmp = jobA.localeCompare(jobB, 'ko');
      return sortOrder.value === 'asc' ? cmp : -cmp;
    }
    if (sortKey.value === 'monthlyPay') {
      const cmp = getListSortValue(a, 'monthlyPay') - getListSortValue(b, 'monthlyPay');
      return sortOrder.value === 'asc' ? cmp : -cmp;
    }
    if (sortKey.value === 'corpInsurance') {
      const cmp = getListSortValue(a, 'corpInsurance') - getListSortValue(b, 'corpInsurance');
      return sortOrder.value === 'asc' ? cmp : -cmp;
    }
    if (sortKey.value === 'retirement') {
      const cmp = getListSortValue(a, 'retirement') - getListSortValue(b, 'retirement');
      return sortOrder.value === 'asc' ? cmp : -cmp;
    }
    const rankA = getJobRank(a.ce_position || '');
    const rankB = getJobRank(b.ce_position || '');
    if (rankA !== rankB) return rankA - rankB;
    return compareJobThenName(jobA, nameA, jobB, nameB);
  });

  return list;
});

const excludedEmployees = computed(() => {
  const list = filterWageRowsBySearch(wageRows.value.filter((row) => isListExcluded(row)));
  list.sort((a, b) =>
    compareJobThenName(a.ce_position || '', a.ce_name || '', b.ce_position || '', b.ce_name || '')
  );
  return list;
});

const filteredRows = computed(() => sortedEmployees.value);

/** 저장된 행 — 임금합계 필드가 0이어도 항목 합이 있으면 대장 대상 (제거된 사원 제외) */
const savedWageRows = computed(() =>
  wageRows.value.filter(
    (row) =>
      !isListExcluded(row) &&
      row.cwhd_idx != null &&
      (Number(row.cwhd_wage_total) > 0 || sumCommonWagePayParts(row) > 0)
  )
);

const toggleSort = (key: Exclude<ListSortKey, 'default'>) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortOrder.value = 'asc';
  }
};

const getSortIcon = (key: string) => {
  if (sortKey.value !== key) return 'mdi-unfold-more-horizontal';
  return sortOrder.value === 'asc' ? 'mdi-chevron-up' : 'mdi-chevron-down';
};

const avatarColors = [
  '#1e3a5f', '#2e7d32', '#c62828', '#f57c00', '#6a1b9a',
  '#00838f', '#4527a0', '#ad1457', '#00695c', '#bf360c',
];

const getAvatarColor = (name?: string | null) => {
  if (!name || name === '-') return '#90a4ae';
  return avatarColors[name.charCodeAt(0) % avatarColors.length];
};

const getInitial = (name?: string | null) => (name && name !== '-' ? name.charAt(0) : '?');

const formatListWageAmount = (
  row: CommonWageListRow,
  field: 'monthlyPay' | 'corpInsurance' | 'retirement'
) => {
  const val = getListSortValue(row, field);
  return val > 0 ? formatNum(val) : '-';
};

const isSelectedEmployee = (row: CommonWageListRow) =>
  selectedRow.value != null && rowEmpKey(selectedRow.value) === rowEmpKey(row);

const selectEmployee = (row: CommonWageListRow) => {
  // ★ 저장하지 않은 변경사항 확인
  if (!confirmUnsavedChanges()) return;
  clearDirty();
  
  const key = rowEmpKey(row);
  selectedRow.value = wageRows.value.find((r) => rowEmpKey(r) === key) ?? row;
  mainView.value = 'detail';
};

const showAllList = () => {
  // ★ 저장하지 않은 변경사항 확인
  if (!confirmUnsavedChanges()) return;
  clearDirty();
  
  mainView.value = 'list';
  selectedRow.value = null;
};

const selectedEmployeeIndex = computed(() => {
  if (!selectedRow.value) return -1;
  const key = rowEmpKey(selectedRow.value);
  return sortedEmployees.value.findIndex((row) => rowEmpKey(row) === key);
});

const canNavigatePrevEmployee = computed(() => selectedEmployeeIndex.value > 0);

const canNavigateNextEmployee = computed(() => {
  const idx = selectedEmployeeIndex.value;
  return idx >= 0 && idx < sortedEmployees.value.length - 1;
});

const isEmployeeNavDisabled = computed(
  () => listLoading.value || bulkLoading.value || savingDetail.value || importingPrevMonth.value
);

const navigatePrevEmployee = () => {
  const idx = selectedEmployeeIndex.value;
  if (idx <= 0 || isEmployeeNavDisabled.value) return;
  selectEmployee(sortedEmployees.value[idx - 1]);
};

const navigateNextEmployee = () => {
  const idx = selectedEmployeeIndex.value;
  if (idx < 0 || idx >= sortedEmployees.value.length - 1 || isEmployeeNavDisabled.value) return;
  selectEmployee(sortedEmployees.value[idx + 1]);
};

const savedWageCount = computed(() => savedWageRows.value.length);

const selectedYm = computed(
  () => `${selectedYear.value}-${String(selectedMonth.value).padStart(2, '0')}`
);

/** 입사일·퇴사일 → YYYY-MM (DB Date·ISO·YYYY.MM.DD 등 통일) */
const dateToYm = (dateVal: string | Date | null | undefined): string | null => {
  if (dateVal == null || dateVal === '') return null;
  let head: string;
  if (dateVal instanceof Date) {
    if (Number.isNaN(dateVal.getTime())) return null;
    head = `${dateVal.getFullYear()}-${String(dateVal.getMonth() + 1).padStart(2, '0')}-${String(dateVal.getDate()).padStart(2, '0')}`;
  } else {
    head = String(dateVal).trim().substring(0, 10).replace(/\./g, '-').replace(/\//g, '-');
  }
  const m = head.match(/^(\d{4})-(\d{1,2})/);
  if (!m) return null;
  return `${m[1]}-${String(m[2]).padStart(2, '0')}`;
};

const isJoinMonth = (item: CommonWageListRow) => dateToYm(item.ce_join_date) === selectedYm.value;

const isLeaveMonth = (item: CommonWageListRow) => dateToYm(item.ce_leave_date) === selectedYm.value;

/** 선택 연월 말일 기준 근로개월수 */
const getEmpTenureMonths = (row: CommonWageListRow | null): number | null => {
  if (!row) return null;
  const raw = row.ce_join_date || row.cwhd_join_date;
  const join = parseJoinDate(raw as string);
  if (!join) return null;
  return calcTenureMonths(join, selectedYear.value, selectedMonth.value);
};

/** 툴바 칩용 근무 개월수 표시 */
const formatEmpTenureChip = (row: CommonWageListRow | null): string => {
  const months = getEmpTenureMonths(row);
  if (months === null) return '-';
  return `${months}개월차`;
};

/** 만나이 계산 */
const getEmpAge = (row: CommonWageListRow | null): number | null => {
  if (!row) return null;
  const birth = row.ce_birth_date || row.cwhd_birth;
  if (!birth) return null;
  const birthDate = new Date(birth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= 0 ? age : null;
};

/** 만나이 칩 표시 */
const formatEmpAgeChip = (row: CommonWageListRow | null): string | null => {
  const age = getEmpAge(row);
  if (age === null) return null;
  return `만${age}세`;
};

const formatNum = (val: number | null | undefined) => {
  const num = Number(val) || 0;
  return num.toLocaleString('ko-KR');
};

const getDefaultPayDate = () => {
  let payYear = selectedYear.value;
  let payMonth = selectedMonth.value + 1;
  if (payMonth > 12) {
    payMonth = 1;
    payYear += 1;
  }
  return `${payYear}-${String(payMonth).padStart(2, '0')}-10`;
};

const redirectIfHomeCare = () => {
  if (!selectedBizGubun.value || !isHomeCareBiz(selectedBizGubun.value)) return;
  const params = isPopupMode.value ? { memberCode: memberCode.value } : undefined;
  router.replace({ name: homeRouteName.value, params });
};

const resolveSanRate = (data: { sanRate?: number | null; cwp_san_rate?: number | null } | null | undefined) => {
  const raw = data?.sanRate ?? data?.cwp_san_rate;
  return raw != null && Number(raw) > 0 ? Number(raw) : null;
};

const applyMonthlySetting = (data: { payDate?: string | null; sanRate?: number | null } | null) => {
  if (data?.payDate) {
    payDate.value = String(data.payDate).substring(0, 10);
  } else {
    payDate.value = getDefaultPayDate();
  }
  const rate = resolveSanRate(data);
  if (rate != null) {
    sanRate.value = rate;
    sanRateDisplay.value = formatSanRateDisplay(rate);
  }
};

const fetchPreviousSanRate = async () => {
  if (!memberCode.value) {
    sanRate.value = 0.0068;
    sanRateDisplay.value = '0.68';
    return;
  }
  for (let i = 1; i <= 12; i++) {
    let targetYear = selectedYear.value;
    let targetMonth = selectedMonth.value - i;
    if (targetMonth <= 0) {
      targetYear -= 1;
      targetMonth += 12;
    }
    try {
      const response = await commonWageApi.getMonthlySetting(
        memberCode.value,
        targetYear,
        targetMonth,
        selectedBizGubun.value
      );
      const rate = resolveSanRate(response.data?.data);
      if (response.data?.success && rate != null) {
        sanRate.value = rate;
        sanRateDisplay.value = formatSanRateDisplay(rate);
        return;
      }
    } catch {
      // continue
    }
  }
  sanRate.value = 0.0068;
  sanRateDisplay.value = '0.68';
};

const loadMonthlySetting = async () => {
  if (!memberCode.value) return;
  try {
    const response = await commonWageApi.getMonthlySetting(
      memberCode.value,
      selectedYear.value,
      selectedMonth.value,
      selectedBizGubun.value
    );
    const data = response.data?.data;
    if (response.data?.success && data) {
      applyMonthlySetting(data);
      if (resolveSanRate(data) == null) {
        await fetchPreviousSanRate();
      }
    } else {
      payDate.value = getDefaultPayDate();
      await fetchPreviousSanRate();
    }
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    const status = error.response?.status || '';
    alert(`월별 설정 조회 실패: ${message}${status ? ` (${status})` : ''}`);
    payDate.value = getDefaultPayDate();
    await fetchPreviousSanRate();
  }
};

const restoreDetailEmployee = (empKey: string | null) => {
  if (!empKey || mainView.value !== 'detail') return;
  selectedRow.value = wageRows.value.find((row) => rowEmpKey(row) === empKey) || null;
  if (!selectedRow.value) showAllList();
};

const loadWageList = async (preserveEmpKey?: string | null) => {
  if (!memberCode.value) return;
  const loadSeq = ++monthDataLoadSeq.value;
  listLoading.value = true;
  tableDraft.clearDrafts();
  try {
    const response = await commonWageApi.getList(memberCode.value, {
      year: selectedYear.value,
      month: selectedMonth.value,
      bizGubun: selectedBizGubun.value,
    });
    if (loadSeq !== monthDataLoadSeq.value) return;
    if (!response.data?.success) {
      throw new Error(response.data?.message || '목록 조회 실패');
    }
    wageRows.value = response.data.data || [];
    stats.value = response.data.stats || {
      totalCount: wageRows.value.length,
      totalWage: 0,
      totalLDeduction: 0,
      totalCDeduction: 0,
    };
  } catch (error: any) {
    if (loadSeq !== monthDataLoadSeq.value) return;
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    const status = error.response?.status || '';
    alert(`임금 목록 조회 실패: ${message}${status ? ` (${status})` : ''}`);
    wageRows.value = [];
    stats.value = { totalCount: 0, totalWage: 0, totalLDeduction: 0, totalCDeduction: 0 };
  } finally {
    if (loadSeq !== monthDataLoadSeq.value) return;
    if (preserveEmpKey) restoreDetailEmployee(preserveEmpKey);
    listLoading.value = false;
  }
};

const loadIncomeTax = async () => {
  const loadSeq = monthDataLoadSeq.value;
  try {
    const res = await commonWageApi.getIncomeTax(selectedYear.value);
    if (loadSeq !== monthDataLoadSeq.value) return;
    if (res.data?.success && Array.isArray(res.data.data)) {
      incomeTaxTable.value = res.data.data.map((row: { income1: number; income2: number; depe_1: number }) => ({
        income1: Number(row.income1),
        income2: Number(row.income2),
        depe_1: Number(row.depe_1),
      }));
    }
  } catch (error: any) {
    console.error('간이세액표 로드 오류:', error);
  }
};

const loadReferenceData = async () => {
  const loadSeq = monthDataLoadSeq.value;
  try {
    const taxRes = await commonWageApi.getTaxRate(selectedYear.value);
    if (loadSeq !== monthDataLoadSeq.value) return;
    if (taxRes.data?.success && taxRes.data.data) {
      taxRateInfo.value = taxRes.data.data;
      taxRateFallback.value = !!taxRes.data.fallback;
    }
    void loadIncomeTax();
  } catch (error: any) {
    if (loadSeq !== monthDataLoadSeq.value) return;
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`세율 조회 실패: ${message}`);
  }
};

const loadPageData = async (preserveEmpKey?: string | null) => {
  if (!memberCode.value || isHomeCareBiz(selectedBizGubun.value)) return;
  await Promise.all([
    loadMonthlySetting(),
    loadWageList(preserveEmpKey),
    loadReferenceData(),
  ]);
};

const saveMonthSettingsInternal = async () => {
  if (!memberCode.value) return;
  const parsed = parseHomeWageRatePercentInput(sanRateDisplay.value);
  if (parsed == null) {
    throw new Error('산재요율을 확인해 주세요.');
  }
  sanRate.value = parsed;
  sanRateDisplay.value = formatSanRateDisplay(parsed);
  const response = await commonWageApi.saveMonthlySetting({
    mCode: memberCode.value,
    bizGubun: selectedBizGubun.value,
    year: selectedYear.value,
    month: selectedMonth.value,
    payDate: payDate.value || null,
    sanRate: parsed,
  });
  if (!response.data?.success) {
    throw new Error(response.data?.message || '저장 실패');
  }
};

const handleSaveMonthSettings = async () => {
  if (!memberCode.value) return;
  if (isWageMagamClosed.value) {
    alert('마감된 연월은 수정할 수 없습니다.\n마감을 해제한 뒤 다시 시도해 주세요.');
    return;
  }
  savingMonthSettings.value = true;
  try {
    await saveMonthSettingsInternal();
    alert('지급일·산재요율이 저장되었습니다.');
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`지급일·산재요율 저장 실패: ${message}`);
  } finally {
    savingMonthSettings.value = false;
  }
};

const onDetailSaved = async () => {
  const key =
    selectedRow.value && mainView.value === 'detail' ? rowEmpKey(selectedRow.value) : null;
  await loadWageList(key);
};

const removingEmployeeKey = ref<string | null>(null);

/** 저장된 당월 임금이 있는 사원만 목록에서 제거 가능 (전월복사·저장 후) */
const canRemoveEmployeeFromList = (row: CommonWageListRow | null | undefined) =>
  !!row?.cwhd_idx && !isWageReadOnly.value && !isListExcluded(row!);

const removeEmployeeFromList = async (row: CommonWageListRow) => {
  if (!row?.cwhd_idx) {
    alert('아직 저장된 임금이 없는 사원입니다. 전월복사·저장 후에 목록에서 제거할 수 있습니다.');
    return;
  }
  if (isWageReadOnly.value) {
    alert('마감된 연월은 수정할 수 없습니다.\n마감을 해제한 뒤 다시 시도해 주세요.');
    return;
  }
  const name = row.ce_name || '해당 사원';
  if (
    !confirm(
      `${name} 님을 이번 달 임금 사원 목록에서 제거할까요?\n(하단 회색 목록으로 내려가며, 언제든 복원할 수 있습니다.)`
    )
  ) {
    return;
  }

  const key = rowEmpKey(row);
  removingEmployeeKey.value = key;
  try {
    const res = await commonWageApi.delete(row.cwhd_idx);
    if (!res.data?.success) throw new Error(res.data?.message || '삭제 실패');
    clearDirty();
    if (selectedRow.value && rowEmpKey(selectedRow.value) === key) {
      showAllList();
    }
    await loadWageList();
  } catch (error: any) {
    alert(`목록에서 제거 실패: ${error.response?.data?.message || error.message}`);
  } finally {
    removingEmployeeKey.value = null;
  }
};

const restoreEmployeeToList = async (row: CommonWageListRow) => {
  if (!row?.cwhd_idx) return;
  if (isWageReadOnly.value) {
    alert('마감된 연월은 수정할 수 없습니다.\n마감을 해제한 뒤 다시 시도해 주세요.');
    return;
  }
  const key = rowEmpKey(row);
  removingEmployeeKey.value = key;
  try {
    const res = await commonWageApi.restore(row.cwhd_idx);
    if (!res.data?.success) throw new Error(res.data?.message || '복원 실패');
    await loadWageList();
  } catch (error: any) {
    alert(`복원 실패: ${error.response?.data?.message || error.message}`);
  } finally {
    removingEmployeeKey.value = null;
  }
};

const onDetailDeleted = async () => {
  clearDirty();
  showAllList();
  await loadWageList();
};

const importPrevMonthForSelected = async () => {
  if (!selectedRow.value || !wageEditModalRef.value) return;
  importingPrevMonth.value = true;
  try {
    await wageEditModalRef.value.importPrevMonthData();
  } finally {
    importingPrevMonth.value = false;
  }
};

const saveSelectedWage = async () => {
  if (!selectedRow.value || !wageEditModalRef.value) return;
  if (isWageMagamClosed.value) {
    alert('마감된 연월은 수정할 수 없습니다.\n마감을 해제한 뒤 다시 시도해 주세요.');
    return;
  }
  const key = rowEmpKey(selectedRow.value);
  savingDetail.value = true;
  try {
    await wageEditModalRef.value.saveWage();
    await loadWageList(key);
    clearDirty(); // ★ 저장 성공 후 dirty 플래그 해제
  } finally {
    savingDetail.value = false;
  }
};

const onWageReportError = (message: string) => {
  alert(`임금대장 생성 실패: ${message}`);
};

const downloadAccountingReport = () => {
  if (!canViewAccountingReport.value) {
    alert('회계처리용 임금대장은 매니저 이상 권한이 필요합니다.');
    return;
  }
  if (savedWageCount.value === 0) {
    alert('다운로드할 임금 데이터가 없습니다.');
    return;
  }
  wageReportRef.value?.openPreview('accounting');
};

const downloadClientReport = () => {
  if (savedWageCount.value === 0) {
    alert('다운로드할 임금 데이터가 없습니다.');
    return;
  }
  wageReportRef.value?.openPreview('client');
};

const openNontaxCompare = () => {
  if (savedWageCount.value === 0) {
    alert('비과세 분석할 임금 데이터가 없습니다.');
    return;
  }
  nontaxCompareRef.value?.openDialog();
};

const openNontaxCompareYearly = () => {
  if (!memberCode.value) {
    alert('시설 정보를 찾을 수 없습니다.');
    return;
  }
  nontaxCompareYearlyRef.value?.openDialog();
};

const openNontaxSavings = () => {
  if (savedWageCount.value === 0) {
    alert('절감액을 볼 임금 데이터가 없습니다.');
    return;
  }
  nontaxCompareRef.value?.openDialog();
};

const openLaborCostModal = async () => {
  if (!memberCode.value) {
    alert('시설 코드가 없습니다.');
    return;
  }
  loadingLaborCostModal.value = true;
  try {
    await laborCostModalRef.value?.open();
  } finally {
    loadingLaborCostModal.value = false;
  }
};

const openTaxRateEditModal = () => {
  const t = taxRateInfo.value;
  if (!t || !canEditTaxRate.value) return;
  taxRateEditTargetYear.value = selectedYear.value;
  taxRateEditForm.value = {
    rateYeon: formatHomeWageRatePercent(t.rateYeon, { withSuffix: false }),
    rateGun: formatHomeWageRatePercent(t.rateGun, { withSuffix: false }),
    rateGo: formatHomeWageRatePercent(t.rateGo, { withSuffix: false }),
    rateJang: formatHomeWageRatePercent(t.rateJang, { withSuffix: false }),
    rateSil: formatHomeWageRatePercent(t.rateSil, { withSuffix: false }),
    rateSan: formatHomeWageRatePercent(t.rateSan, { withSuffix: false }),
    laborCostRate: formatHomeWageRatePercent(t.laborCostRate, { scaleFromDecimal: false, withSuffix: false }),
  };
  showTaxRateEditModal.value = true;
};

const closeTaxRateEditModal = () => {
  if (savingTaxRate.value) return;
  showTaxRateEditModal.value = false;
};

const onTaxRateEditBlur = (key: TaxRateEditFieldKey) => {
  const field = taxRateEditFields.find((f) => f.key === key);
  if (!field) return;
  const raw = taxRateEditForm.value[key];
  if (field.isLaborRate) {
    const trimmed = String(raw).trim().replace(/%/g, '');
    if (!trimmed) {
      taxRateEditForm.value[key] = '';
      return;
    }
    const n = parseFloat(trimmed);
    taxRateEditForm.value[key] = Number.isFinite(n) ? String(n) : '';
    return;
  }
  const parsed = parseHomeWageRatePercentInput(raw);
  taxRateEditForm.value[key] = parsed != null ? formatHomeWageRatePercent(parsed, { withSuffix: false }) : '';
};

const saveTaxRateEdit = async () => {
  if (savingTaxRate.value) return;
  const f = taxRateEditForm.value;
  const rateKeys: TaxRateEditFieldKey[] = ['rateYeon', 'rateGun', 'rateGo', 'rateJang', 'rateSil', 'rateSan'];
  for (const key of rateKeys) {
    const parsed = parseHomeWageRatePercentInput(f[key]);
    if (parsed == null) {
      alert(`${taxRateEditFields.find((item) => item.key === key)?.label || key} 요율을 확인해 주세요.`);
      return;
    }
  }
  const laborTrimmed = String(f.laborCostRate).trim().replace(/%/g, '');
  const laborCostRate = laborTrimmed ? parseFloat(laborTrimmed) : NaN;
  if (!Number.isFinite(laborCostRate) || laborCostRate < 0) {
    alert('인건비율을 확인해 주세요.');
    return;
  }
  savingTaxRate.value = true;
  try {
    const currentMinHourly = taxRateInfo.value?.minHourlyWage ?? 0;
    const response = await commonWageApi.saveTaxRate(taxRateEditTargetYear.value, {
      minHourlyWage: currentMinHourly,
      rateYeon: parseHomeWageRatePercentInput(f.rateYeon) ?? 0,
      rateGun: parseHomeWageRatePercentInput(f.rateGun) ?? 0,
      rateGo: parseHomeWageRatePercentInput(f.rateGo) ?? 0,
      rateJang: parseHomeWageRatePercentInput(f.rateJang) ?? 0,
      rateSil: parseHomeWageRatePercentInput(f.rateSil) ?? 0,
      rateSan: parseHomeWageRatePercentInput(f.rateSan) ?? 0,
      laborCostRate,
    });
    if (response.data?.success) {
      await loadReferenceData();
      showTaxRateEditModal.value = false;
    } else {
      alert(`세율 저장 실패: ${response.data?.message || '알 수 없는 오류'}`);
    }
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`세율 저장 실패: ${message}`);
  } finally {
    savingTaxRate.value = false;
  }
};

// SMS 비활성 — 활성화 시 주석 해제
/*
const openSmsModal = () => {
  if (savedWageCount.value === 0) {
    alert('SMS 발송할 임금 데이터가 없습니다.');
    return;
  }
  smsModalRef.value?.openDialog();
};

const openSmsTestModal = () => {
  smsTestModalRef.value?.openDialog();
};
*/

const openTransactionModal = () => {
  transactionRef.value?.openDialog(filteredRows.value.length);
};

const syncUrlYearMonth = () => {
  router.replace({
    query: {
      ...route.query,
      year: String(selectedYear.value),
      month: String(selectedMonth.value),
    },
  });
};

const saveAllData = async () => {
  if (isWageMagamClosed.value) {
    alert('마감된 연월은 수정할 수 없습니다.\n마감을 해제한 뒤 다시 시도해 주세요.');
    return;
  }
  if (!memberCode.value || !taxRateInfo.value) {
    alert('시설 또는 세율 정보를 불러올 수 없습니다.');
    return;
  }
  const allRows = filteredRows.value.filter((r) => r.ce_name);
  const savedItems = allRows.filter((r) => r.cwhd_idx).map((r) => tableDraft.mergeDraftIntoRow(r));
  const unsavedCount = allRows.length - savedItems.length;

  if (allRows.length === 0) {
    alert('저장할 직원이 없습니다.');
    return;
  }

  let confirmMsg = `저장된 임금 ${savedItems.length}건을 저장합니다.\n(요양보호사·조리는 연·야·휴 비과세, 중식비 실비변상 시 회계 중식 채움·그 과세로 공제 재산출 — 중도 입·퇴사는 재직일 비율)`;
  if (savedItems.length === 0) {
    alert('저장할 임금 데이터가 없습니다.\n상세에서 입력 후 「저장」하거나, 이미 저장된 직원이 있어야 합니다.');
    return;
  }
  if (unsavedCount > 0) {
    confirmMsg += `\n\n미저장 ${unsavedCount}명은 상세에서 「저장」해야 반영됩니다.`;
  }
  confirmMsg += '\n\n계속하시겠습니까?';

  if (!confirm(confirmMsg)) {
    return;
  }

  savingAll.value = true;
  savingAllProgress.value = { current: 0, total: Math.max(savedItems.length, 1) };
  bulkLoading.value = true;

  try {
    let result = { successCount: 0, errorCount: 0, skippedCount: 0, errors: [] as string[] };
    if (savedItems.length > 0) {
      result = await bulkSaveCommonWageRows({
        rows: savedItems,
        mCode: memberCode.value,
        bizGubun: selectedBizGubun.value,
        year: selectedYear.value,
        month: selectedMonth.value,
        taxRateInfo: effectiveTaxRateInfo.value || taxRateInfo.value,
        incomeTaxTable: incomeTaxTable.value,
        minHourlyWage: taxRateInfo.value.minHourlyWage,
        defaultWorkDays: defaultWorkDays.value,
        onProgress: (current, total) => {
          savingAllProgress.value = { current, total };
        },
      });
    }

    tableDraft.clearDrafts();

    if (result.errorCount === 0) {
      const skipNote =
        result.skippedCount > 0 ? `\n\n※ 미저장 ${result.skippedCount}명은 건너뜀` : '';
      alert(`저장 완료!\n✓ 임금 갱신: ${result.successCount}명${skipNote}`);
    } else {
      alert(
        `저장 완료 (일부 오류)\n\n✓ 임금 성공: ${result.successCount}명\n✗ 실패: ${result.errorCount}명\n\n` +
          result.errors.slice(0, 5).join('\n') +
          (result.errors.length > 5 ? `\n... 외 ${result.errors.length - 5}건` : '')
      );
    }
    await loadWageList();
  } catch (error: any) {
    alert(`저장 실패: ${error.response?.data?.message || error.message}`);
  } finally {
    savingAll.value = false;
    bulkLoading.value = false;
  }
};

const downloadWageSlip = (options?: { name?: string; birth?: string | null }) => {
  if (!memberCode.value) return;
  if (!options?.name && savedWageCount.value === 0) {
    alert('임금명세서를 생성할 데이터가 없습니다.');
    return;
  }
  const token = tokenManager.getToken();
  if (!token) {
    alert('로그인이 필요합니다.');
    return;
  }
  const params = new URLSearchParams();
  params.append('token', token);
  if (selectedBizGubun.value) {
    params.append('bizGubun', selectedBizGubun.value);
  }
  if (options?.name) {
    params.append('name', options.name);
  }
  if (options?.birth) {
    params.append('birth', options.birth);
  }
  const url = `/api/common-wage/wage-slip-pdf/${memberCode.value}/${selectedYear.value}/${selectedMonth.value}?${params.toString()}`;
  const previewWindow = window.open(url, '_blank');
  if (!previewWindow) {
    alert('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
  }
};

const downloadWageSlipForSelected = () => {
  const row = selectedRow.value;
  if (!row?.cwhd_idx) {
    alert('저장된 임금 데이터가 없습니다.');
    return;
  }
  downloadWageSlip({
    name: row.cwhd_name || row.ce_name || '',
    birth: resolveAuthoritativeBirth(row),
  });
};

const handleCopyFromPrev = async () => {
  if (!memberCode.value) return;
  if (isWageMagamClosed.value) {
    alert('마감된 연월은 수정할 수 없습니다.\n마감을 해제한 뒤 다시 시도해 주세요.');
    return;
  }
  if (
    !confirm(
      `${selectedYear.value}년 ${selectedMonth.value}월 임금을 전월 근무정보로 복사할까요?\n(이미 입력된 직원은 제외됩니다)\n\n※ 전월 지급 합계(기본급·수당 전액)는 그대로 복사됩니다.\n※ 요양보호사·조리는 방문요양과 같이 연·야·휴 비과세를 다시 나눕니다.\n※ 4대보험·공제는 당월 요율로 자동 계산되어 저장됩니다.`
    )
  ) {
    return;
  }
  bulkLoading.value = true;
  try {
    const res = await commonWageApi.copyFromPrev({
      mCode: memberCode.value,
      bizGubun: selectedBizGubun.value,
      year: selectedYear.value,
      month: selectedMonth.value,
    });
    if (!res.data?.success) throw new Error(res.data?.message || '전월 복사 실패');
    await loadWageList();
    const copiedCount = Number(res.data.copiedCount) || 0;
    const targets = wageRows.value.filter((row) => needsCopiedDeductionRecalc(row));
    let recalcNote = '';
    if (copiedCount > 0 && targets.length > 0 && effectiveTaxRateInfo.value) {
      const recalc = await bulkRecalcCopiedDeductions({
        rows: targets,
        mCode: memberCode.value,
        bizGubun: selectedBizGubun.value,
        year: selectedYear.value,
        month: selectedMonth.value,
        taxRateInfo: effectiveTaxRateInfo.value,
        incomeTaxTable: incomeTaxTable.value,
        minHourlyWage: effectiveTaxRateInfo.value.minHourlyWage,
        defaultWorkDays: defaultWorkDays.value,
      });
      await loadWageList();
      if (recalc.errorCount === 0) {
        recalcNote = `\n4대보험·공제 ${recalc.successCount}명 자동 계산 완료.`;
      } else {
        recalcNote = `\n4대보험 자동 계산: 성공 ${recalc.successCount}명, 실패 ${recalc.errorCount}명.`;
      }
    }
    alert((res.data.message || '복사되었습니다.') + recalcNote);
  } catch (error: any) {
    alert(`전월 복사 실패: ${error.response?.data?.message || error.message}`);
  } finally {
    bulkLoading.value = false;
  }
};

const handleDeleteAllMonth = async () => {
  if (!memberCode.value) return;
  if (
    !confirm(
      `${selectedYear.value}년 ${selectedMonth.value}월 임금 데이터를 모두 삭제할까요?\n이 작업은 되돌릴 수 없습니다.`
    )
  ) {
    return;
  }
  bulkLoading.value = true;
  try {
    const res = await commonWageApi.deleteAllMonth({
      mCode: memberCode.value,
      bizGubun: selectedBizGubun.value,
      year: selectedYear.value,
      month: selectedMonth.value,
    });
    if (!res.data?.success) throw new Error(res.data?.message || '삭제 실패');
    alert(res.data.message || '삭제되었습니다.');
    showAllList();
    await loadWageList();
  } catch (error: any) {
    alert(`당월 삭제 실패: ${error.response?.data?.message || error.message}`);
  } finally {
    bulkLoading.value = false;
  }
};

watch(selectedBizGubun, () => {
  redirectIfHomeCare();
});

watch([selectedYear, selectedMonth], () => {
  syncUrlYearMonth();
});

watch(yearMonth, (ym, prev) => {
  if (prev && ym !== prev) {
    wageRows.value = [];
  }
});

watch([selectedYear, selectedMonth, selectedBizGubun, memberCode], (newVals, oldVals) => {
  if (isHomeCareBiz(selectedBizGubun.value)) return;

  const bizChanged = oldVals != null && newVals[2] !== oldVals[2];
  const memberChanged = oldVals != null && newVals[3] !== oldVals[3];
  if (bizChanged || memberChanged) {
    showAllList();
  }

  const preserveKey =
    !bizChanged && !memberChanged && mainView.value === 'detail' && selectedRow.value
      ? rowEmpKey(selectedRow.value)
      : null;

  void loadPageData(preserveKey);
});

onMounted(() => {
  const qYear = Number(route.query.year);
  const qMonth = Number(route.query.month);
  if (qYear >= 2000 && qYear <= 2100 && qMonth >= 1 && qMonth <= 12) {
    setYearMonth(qYear, qMonth);
  } else if (qYear >= 2000 && qYear <= 2100) {
    setYearMonth(qYear, selectedMonth.value);
  } else if (qMonth >= 1 && qMonth <= 12) {
    setYearMonth(selectedYear.value, qMonth);
  }
  syncUrlYearMonth();

  redirectIfHomeCare();
  if (!isHomeCareBiz(selectedBizGubun.value)) {
    loadPageData();
  }
});
</script>

<style lang="scss">
@import '@/assets/styles/pages/member-facility-wage-monthly-setting';
</style>
